import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import AssignmentBatchRow from "@/components/AssignmentBatchRow";
import WirksamkeitDialog from "@/components/wirksamkeit/WirksamkeitDialog";
import {
  CircleAlert,
  CheckCircle2,
  Clock,
  Plus,
  ArrowRight,
  RotateCw,
} from "lucide-react";

// Tage bis zur Fälligkeit (negativ = überfällig). Identische Logik wie in
// AssignmentBatchRow – KEINE neue Berechnung, nur Wiederverwendung.
function daysUntilDue(iso) {
  if (!iso) return null;
  return Math.floor((new Date(iso) - new Date()) / (1000 * 60 * 60 * 24));
}

const FILTER_LABEL = {
  handlungsbedarf: "Handlungsbedarf",
  bald_faellig: "Bald fällig",
  erledigt: "Erledigt",
  ueberfaellig: "Überfällig",
};

function defaultFilter(s) {
  if (!s) return "handlungsbedarf";
  if (s.ueberfaellig > 0) return "ueberfaellig";
  if (s.bald_faellig > 0) return "bald_faellig";
  return "handlungsbedarf";
}

function StatCard({ label, value, icon: Icon, iconSrc, accent, testId, active, onClick }) {
  const clickable = typeof onClick === "function";
  // MOBIL-NAV-03, Betreiberfreigabe 29.10.2026 — Klassen stat-kachel/-kopf/-links/-bezeichnung/-bild nur für den Handy-Stilblock im Dashboard
  return (
    <Card
      data-testid={testId}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      aria-pressed={clickable ? active : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={`stat-kachel bg-white p-6 transition-all border ${
        clickable ? "cursor-pointer hover:shadow-md hover:border-slate-300" : ""
      } ${
        active
          ? "border-slate-900 ring-2 ring-slate-900 shadow-md"
          : "border-slate-200 shadow-sm"
      }`}
    >
      {/* MOBIL-NAV-03, Betreiberfreigabe 29.10.2026 — Klassen Kopfzeile, linker Block, Bezeichnung */}
      <div className="stat-kachel-kopf flex items-start justify-between">
        <div className="stat-kachel-links">
          <div className="stat-kachel-bezeichnung text-xs uppercase tracking-widest text-slate-500 font-medium">
            {label}
          </div>
          <div className="font-heading text-4xl font-bold text-slate-900 mt-3">
            {value}
          </div>
        </div>
        {/* UI-REDESIGN Ergänzung 3. Kapitel (Aug 2026): freigegebene Foto-
            Icons (Ampel Rot/Amber/Grün, Sanduhr) ersetzen die lucide-Symbole
            in den vier Dashboard-Kacheln. Wenn `iconSrc` gesetzt ist, wird
            das PNG ohne farbige Kachel-Hinterlegung angezeigt (das Foto
            trägt seine Farbstimmung selbst). Fallback: lucide-Icon in der
            farbigen accent-Kachel. */}
        {/* MOBIL-NAV-03, Betreiberfreigabe 29.10.2026 — Klasse stat-kachel-bild */}
        {iconSrc ? (
          <img
            src={iconSrc}
            alt=""
            className="stat-kachel-bild w-14 h-14 object-contain shrink-0 select-none"
            draggable="false"
            data-testid={`${testId}-icon-img`}
          />
        ) : (
          <div className={`w-10 h-10 rounded-md flex items-center justify-center ${accent}`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * UnternehmerDashboard (UX 2.0 – Aufgabenfokus)
 *
 * Das Dashboard beantwortet nur: „Muss ich jetzt etwas tun?"
 * KPI-Karten filtern die Aufgabenliste; standardmäßig wird der dringendste
 * Zustand angezeigt. Wiedervorlage-Aufgaben erscheinen erst im bestehenden
 * Vorwarnzeitraum (30 Tage, gleiche Fälligkeitslogik wie zuvor).
 */
export default function UnternehmerDashboard() {
  const [stats, setStats] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [filter, setFilter] = useState(null);
  // P1 (Juni 2026) – Wirksamkeitskontrolle (W-A1): Zähler + Prüfmaske-Dialog.
  const [wkSummary, setWkSummary] = useState(null);
  const [wkOpen, setWkOpen] = useState(false);
  const [ownerName, setOwnerName] = useState("");
  // Handschutzplan-Modul · Baustein 7 (Aug 2026): Read-Time-Fälligkeits-
  // Summary (kein Job, kein persistierter Zustand). Fehlertolerant — für
  // Mandanten ohne hazmat_addon liefert das Server-Gate 403 → kein Hinweis.
  const [hspFaellig, setHspFaellig] = useState(null);

  const refreshWk = async () => {
    try {
      setWkSummary(await api.getWirksamkeitSummary());
    } catch {
      /* read-only Kachel – Fehler nicht eskalieren */
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      const [s, a] = await Promise.all([api.getStats(), api.listAssignments()]);
      if (!alive) return;
      setStats(s);
      setAssignments(a);
      setFilter((prev) => prev ?? defaultFilter(s));
    })();
    // Additive, fehlertolerante Nebenabfragen (dürfen das Dashboard nie blockieren).
    api.getWirksamkeitSummary().then((w) => alive && setWkSummary(w)).catch(() => {});
    api.getCompany().then((c) => alive && setOwnerName(c?.contact_person || "")).catch(() => {});
    api.getHandschutzplanFaelligkeit().then((h) => alive && setHspFaellig(h)).catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const activeFilter = filter || "handlungsbedarf";

  // Sichtbare Aufgaben: erst Wiedervorlage-Sichtbarkeit (nur im Vorwarnzeitraum),
  // dann der aktive KPI-Filter. Reine Frontend-Filterung bestehender Felder.
  const visibleAssignments = useMemo(() => {
    const recurrenceVisible = (a) => {
      if (!a.predecessor_id) return true;
      if (a.status !== "offen") return true; // erledigt/überfällig immer sichtbar
      const d = daysUntilDue(a.due_date);
      return d !== null && d <= 30; // Folge-Aufgabe erst im Vorwarnzeitraum
    };
    const matchesFilter = (a) => {
      if (activeFilter === "handlungsbedarf")
        return a.action_category === "handlungsbedarf";
      if (activeFilter === "erledigt") return a.status === "erledigt";
      if (activeFilter === "ueberfaellig") return a.status === "ueberfaellig";
      if (activeFilter === "bald_faellig") {
        if (a.status !== "offen") return false;
        const d = daysUntilDue(a.due_date);
        return d !== null && d >= 0 && d <= 30;
      }
      return true;
    };
    return assignments.filter((a) => recurrenceVisible(a) && matchesFilter(a));
  }, [assignments, activeFilter]);

  // Sichtbare Aufgaben zu Sammelvorgängen gruppieren (per batch_id).
  const batches = useMemo(() => {
    const map = new Map();
    for (const a of visibleAssignments) {
      const key = a.batch_id || `solo-${a.id}`;
      if (!map.has(key)) {
        map.set(key, {
          batch_id: key,
          course: a.course,
          due_date: a.due_date,
          assigned_at: a.assigned_at,
          assignments: [],
        });
      }
      map.get(key).assignments.push(a);
    }
    const list = Array.from(map.values()).map((b) => {
      const counts = { offen: 0, erledigt: 0, ueberfaellig: 0 };
      for (const a of b.assignments) counts[a.status] = (counts[a.status] || 0) + 1;
      return { ...b, counts };
    });
    list.sort((a, b) => (b.assigned_at || "").localeCompare(a.assigned_at || ""));
    return list;
  }, [visibleAssignments]);

  // Dynamische Statusmeldung: Überfällig (rot, dringlich) > bald fällig
  // (ruhig/entlastend, Fokus Automatisierung) > Alles im Plan.
  const headline = useMemo(() => {
    if (!stats) return { text: "Lade Status…", color: "text-slate-900", dot: null };
    if (stats.ueberfaellig > 0) {
      const n = stats.ueberfaellig;
      return {
        text: `${n} ${n === 1 ? "Unterweisung ist" : "Unterweisungen sind"} überfällig.`,
        color: "text-red-700",
        dot: "bg-red-500",
      };
    }
    if (stats.bald_faellig > 0) {
      return {
        text: "Keine überfälligen Unterweisungen.",
        color: "text-emerald-700",
        dot: "bg-emerald-500",
        subDueSoon: stats.bald_faellig,
      };
    }
    return { text: "Alles im Plan.", color: "text-emerald-700", dot: "bg-emerald-500" };
  }, [stats]);

  const FILTER_CARDS = [
    { key: "handlungsbedarf", label: "Handlungsbedarf", value: stats?.handlungsbedarf, icon: Clock, iconSrc: "/ampel/ampel-amber.png", accent: "bg-amber-100 text-amber-700", testId: "stat-handlungsbedarf" },
    { key: "bald_faellig", label: "Bald fällig", value: stats?.bald_faellig, icon: RotateCw, iconSrc: "/kachel-icons/sanduhr.png", accent: "bg-sky-100 text-sky-700", testId: "stat-bald-faellig" },
    { key: "erledigt", label: "Erledigt", value: stats?.erledigt, icon: CheckCircle2, iconSrc: "/ampel/ampel-gruen.png", accent: "bg-emerald-100 text-emerald-700", testId: "stat-erledigt" },
    { key: "ueberfaellig", label: "Überfällig", value: stats?.ueberfaellig, icon: CircleAlert, iconSrc: "/ampel/ampel-rot.png", accent: "bg-red-100 text-red-700", testId: "stat-ueberfaellig" },
  ];

  // P1 (Juni 2026) – WK-Status als sekundärer Aktionsbereich im Hero.
  // Nutzt ausschließlich vorhandene Summary-Zähler (keine neue Berechnung).
  // Sichtbar nur, wenn mindestens ein Wert > 0 ist (sonst kein Mehrwert).
  const wkDueSoon = wkSummary?.due_soon_count ?? 0;
  const wkDeviations = wkSummary?.deviation_count ?? 0;
  const wkOverdue = wkSummary?.overdue_count ?? 0;
  const wkOpenCount = wkSummary?.open_count ?? 0;
  const wkVisible = wkDueSoon > 0 || wkDeviations > 0 || wkOverdue > 0;

  // Baustein 7: Handschutzplan-Hinweis — rot bei überfällig, amber bei nur
  // bald fällig; Klick führt zur Übersichtsseite (Owner entscheidet dort
  // selbst über die Neugenerierung — kein Automatik-Trigger).
  const hspUeber = hspFaellig?.ueberfaellig ?? 0;
  const hspBald = hspFaellig?.bald_faellig ?? 0;
  const hspVisible = hspUeber > 0 || hspBald > 0;
  const hspStatusText = [
    hspUeber > 0 && `${hspUeber} überfällig`,
    hspBald > 0 && `${hspBald} bald fällig`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="unternehmer-dashboard">
      {/* Header: Desktop zweispaltig (Text links, Aktionen rechts), Mobile gestapelt */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="min-w-0 max-w-2xl">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
            Unterweisungsübersicht
          </p>
          <h1
            data-testid="dashboard-status-headline"
            className={`font-heading text-3xl sm:text-4xl font-bold mt-2 flex items-center gap-3 ${headline.color}`}
          >
            {headline.dot && (
              <span className={`inline-block w-3.5 h-3.5 rounded-full ${headline.dot}`} aria-hidden="true" />
            )}
            {headline.text}
          </h1>
          <p className="text-slate-500 mt-2 text-sm" data-testid="dashboard-status-subline">
            {headline.subDueSoon != null ? (
              <>
                CLartext übernimmt die automatische Wiederholung und Zuweisung von
                <br />
                <strong className="font-semibold text-slate-700">
                  {headline.subDueSoon} Unterweisungen
                </strong>
                . Ihre Mitarbeiter werden rechtzeitig informiert.
              </>
            ) : (
              "Wenn die Prüfung kommt, ist alles da."
            )}
          </p>
        </div>
        <div className="flex flex-col items-stretch gap-2 w-full lg:w-auto shrink-0">
          <Link
            to="/unternehmer/unterweisungen"
            data-testid="open-assign-dialog"
            className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white h-12 px-5 rounded-md text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Unterweisung zuweisen
          </Link>
          {wkVisible && (
            <button
              type="button"
              onClick={() => setWkOpen(true)}
              data-testid="wirksamkeit-pruefen-button"
              className="flex flex-col items-center justify-center gap-0.5 min-h-12 py-2 px-5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <span className="text-sm font-medium leading-tight">
                Wirksamkeitskontrollen prüfen
              </span>
              {/* WK-02, Betreiberfreigabe 18.10. (W1): fällige Kontrollen vorne, alle drei Werte immer. */}
              <span className="text-xs font-normal text-slate-500 leading-tight" data-testid="wirksamkeit-status-line">
                <span data-testid="wirksamkeit-overdue-count">{wkOverdue} zur Prüfung fällig</span>
                {" · "}
                <span data-testid="wirksamkeit-due-soon-count">{wkDueSoon} bald fällig</span>
                {" · "}
                <span data-testid="wirksamkeit-deviation-count">{wkDeviations} in Nachverfolgung</span>
              </span>
            </button>
          )}
          {!wkVisible && wkOpenCount > 0 && (
            <button
              type="button"
              onClick={() => setWkOpen(true)}
              data-testid="wirksamkeit-ansehen-button"
              className="inline-flex items-center justify-center gap-2 min-h-12 py-2 px-5 rounded-md border border-slate-200 bg-white text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors text-sm font-medium"
            >
              Wirksamkeitskontrollen ansehen
            </button>
          )}
          {hspVisible && (
            <Link
              to="/unternehmer/handschutzplan"
              data-testid="hsp-dashboard-hinweis"
              className={`flex flex-col items-center justify-center gap-0.5 min-h-12 py-2 px-5 rounded-md border transition-colors ${
                hspUeber > 0
                  ? "border-red-300 bg-red-50 text-red-800 hover:bg-red-100"
                  : "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
              }`}
            >
              <span className="text-sm font-medium leading-tight">
                Handschutzpläne prüfen
              </span>
              <span
                className="text-xs font-normal leading-tight opacity-80"
                data-testid="hsp-dashboard-hinweis-status"
              >
                {hspStatusText}
              </span>
            </Link>
          )}
          {/* CLARTEXT · A1-B UX-Korrektur (Feb 2026): Aushang-Kachel entfernt.
              Der Sidebar-Eintrag `nav-aushang` (UnternehmerLayout) ist der
              einzige Owner-Zugangsweg zur Aushang-Seite. */}
        </div>
      </div>

      {/* KPI-Karten = Filter */}
      {/* MOBIL-NAV-03, Betreiberfreigabe 29.10.2026 — nur Bildschirm bis 767 px: Bild über Bezeichnung und Wert, 16 px, 0.05em; PDF und ab 768 px unverändert */}
      <style>{`
        @media screen and (max-width: 767px) {
          .stat-kachel { padding: 16px; }
          .stat-kachel-kopf { flex-direction: column; }
          .stat-kachel-links { min-width: 0; }
          .stat-kachel-bezeichnung { letter-spacing: 0.05em; }
          .stat-kachel-bild { order: -1; }
        }
      `}</style>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {FILTER_CARDS.map((c) => (
          <StatCard
            key={c.key}
            label={c.label}
            value={c.value ?? "–"}
            icon={c.icon}
            iconSrc={c.iconSrc}
            accent={c.accent}
            testId={c.testId}
            active={activeFilter === c.key}
            onClick={() => setFilter(c.key)}
          />
        ))}
      </div>

      {/* Aufgaben Liste (gefiltert) */}
      <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">
              Aktuelle Aufgaben
            </h2>
            <p className="text-sm text-slate-500 mt-0.5" data-testid="dashboard-filter-label">
              Gefiltert: {FILTER_LABEL[activeFilter]}
            </p>
          </div>
          <Link
            to="/unternehmer/pruefmappe"
            className="text-sm font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
            data-testid="dashboard-open-pruefmappe"
          >
            Zur BG Prüfmappe <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {assignments.length === 0 ? (
          <div className="px-6 py-12 text-center text-slate-500" data-testid="dashboard-empty-state">
            <p className="text-sm">
              Noch keine Aufgaben zugewiesen. Beginne in der{" "}
              <Link
                to="/unternehmer/unterweisungen"
                className="font-medium text-slate-700 underline-offset-2 hover:underline"
                data-testid="dashboard-empty-state-link"
              >
                Gefährdungsbeurteilungen
              </Link>
              .
            </p>
          </div>
        ) : batches.length === 0 ? (
          <div className="px-6 py-12 text-center text-slate-500" data-testid="dashboard-filter-empty">
            <p className="text-sm">
              Keine Aufgaben im Filter „{FILTER_LABEL[activeFilter]}&ldquo;.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {batches.map((b) => (
              <AssignmentBatchRow key={b.batch_id} batch={b} />
            ))}
          </div>
        )}
      </Card>

      <WirksamkeitDialog
        open={wkOpen}
        onOpenChange={setWkOpen}
        defaultName={ownerName}
        onConfirmed={refreshWk}
      />
    </div>
  );
}
